import { createContext, useContext } from 'react'
import { ApiClient, initApiClient } from '@frontend/lib/request'
import { ListPurchaseOrderlineQuery, UIListPurchaseOrderQuery } from '@frontend-types/clients'

import {
  BasicPurchaseOrder,
  EnhancedPurchaseOrder,
  PlaceholderPOCreationPayload,
  PlannedPurchaseOrders,
  PlannedPurchaseOrdersFilters,
  PurchaseOrder,
  PurchaseOrderLine,
  PurchaseOrderUrgencyPayload,
} from '@shared-types/purchase_orders'
import {
  AddAttachmentV2Payload,
  AttachmentV2,
  AttachmentV2UpdatePayload,
} from '@shared-types/shipments'
import { Collection, Identifier } from '@shared-types/util'

export interface PurchaseOrderClient {
  all: (filters: ListPurchaseOrderlineQuery) => Promise<Collection<PurchaseOrderLine>>
  getPurchaseOrder: (id: Identifier) => Promise<BasicPurchaseOrder>
  getPlannedPurchaseOrders: (
    companyId: Identifier,
    filters: PlannedPurchaseOrdersFilters
  ) => Promise<Collection<PlannedPurchaseOrders>>
  allPurchaseOrders: (
    filters: UIListPurchaseOrderQuery
  ) => Promise<Collection<EnhancedPurchaseOrder>>
  createPurchaseOrder: (payload: PlaceholderPOCreationPayload) => Promise<PurchaseOrder>
  updatePurchaseOrderUrgency: (
    id: string,
    payload: PurchaseOrderUrgencyPayload
  ) => Promise<PurchaseOrder>
  addAttachments: (id: string, payload: AddAttachmentV2Payload) => Promise<Collection<AttachmentV2>>
  updateAttachment: (
    id: string,
    attachmentId: string,
    payload: AttachmentV2UpdatePayload
  ) => Promise<Collection<AttachmentV2>>
  deleteAttachment: (id: string, attachmentId: string) => Promise<{ _id: Identifier }>
}

class DefaultPurchaseOrderClient implements PurchaseOrderClient {
  client: ApiClient
  constructor() {
    this.client = initApiClient({})
  }

  async all(filters: ListPurchaseOrderlineQuery = {}) {
    return this.client
      .get(`/purchase_order_lines`, { params: filters })
      .then(({ data }) => data as Collection<PurchaseOrderLine>)
  }

  async allPurchaseOrders(filters: UIListPurchaseOrderQuery) {
    return this.client
      .get('/purchase_orders', { params: filters })
      .then(({ data }) => data as Collection<EnhancedPurchaseOrder>)
  }

  async getPurchaseOrder(id: Identifier) {
    return this.client.get('/purchase_orders/' + id).then(({ data }) => data as BasicPurchaseOrder)
  }

  async createPurchaseOrder(payload: PlaceholderPOCreationPayload) {
    return this.client.post('/purchase_orders', payload).then(({ data }) => data as PurchaseOrder)
  }

  async updatePurchaseOrderUrgency(id: string, payload: PurchaseOrderUrgencyPayload) {
    return this.client
      .put(`/purchase_orders/${id}/urgency`, payload)
      .then(({ data }) => data as PurchaseOrder)
  }

  async getPlannedPurchaseOrders(companyId: Identifier, filters: PlannedPurchaseOrdersFilters) {
    return this.client
      .get(`/companies/${companyId}/planned_purchase_orders`, { params: filters })
      .then(({ data }) => data as Collection<PlannedPurchaseOrders>)
  }

  async addAttachments(id: string, payload: AddAttachmentV2Payload) {
    return this.client
      .post(`/purchase_orders/${id}/attachments`, payload)
      .then(({ data }) => data as Collection<AttachmentV2>)
  }

  async updateAttachment(id: string, attachmentId: string, payload: AttachmentV2UpdatePayload) {
    return this.client
      .patch(`/purchase_orders/${id}/attachments/${attachmentId}`, payload)
      .then(({ data }) => data as Collection<AttachmentV2>)
  }

  async deleteAttachment(id: string, attachmentId: string) {
    return this.client
      .delete(`/purchase_orders/${id}/attachments/${attachmentId}`)
      .then(({ data }) => data as { _id: Identifier })
  }
}

const PurchaseOrderClientContext = createContext<PurchaseOrderClient>(
  new DefaultPurchaseOrderClient()
)

export function usePurchaseOrderClient() {
  return useContext(PurchaseOrderClientContext)
}
